<template>
    <div :class="outerWrapperClass">
        <slot
            name="template"
            :onDeleteClick="onDeleteClick"
            :saving="isSaving"
            :status="status"
            :editing="editing"
            :isSaving="isSaving"
            :text="contentUnit.text"
            :contentUnitId="contentUnit.id"
            :contentUnit="contentUnit"
            :promptImageSelector="promptImageSelector"
            :onTextChange="onTextChange"
            :imageUrl="imageUrl"
            :thumbnailUrl="thumbnailUrl"
            :imageData="imageData">
        </slot>
        <input ref="file-input" type="file" @change="onFileChange" />
        <b-modal
            ref="cropper-modal"
            @ok="onCropDone"
            @close="endCrop"
            @cancel="endCrop"
            @hidden="onCropHidden"
        >
            <vueCropper
                :title="$t('message.crop_the_image')"
                :style="{width: '100%', height: '480px'}"
                ref="cropper"
                :img="imageDataForCropping"
                :autoCrop="true"
                :outputType="cropOutputType"
                :fixed="fixedCropRatio"
                :full="true"
                :autoCropWidth="200"
                :autoCropHeight="200"
                :fixedNumber="[cropAspect, 1]"
            />
        </b-modal>
    </div>
</template>

<script>
    import api from '../mixins/api';
    import promptConfirm from '../mixins/prompt-confirm';
    import loadImage from 'blueimp-load-image';
    import { VueCropper } from 'vue-cropper';
    import { QueueEvent } from '../mixins/queue';
    import Constants from '../constants';

    export default {
        mixins: [api, promptConfirm],

        components: { vueCropper: VueCropper },

        props: {
            context: {
                required: true,
                type: Object
            },
            contentUnit: {
                required: true,
                type: Object
            },
            providerId: {
                required: true,
                type: Number
            },
            providerInformationBlockId: {
                required: false,
            },
            editing: Boolean,
            outerWrapperClass: String,
            enableCrop: Boolean,
            // values like: 1/1, 16/9, 4/3 or null
            cropAspect: {
                default: 1,
                type: Number
            },
            imageMaxLength: {
                default: 4096,
                type: Number
            },
            onContentUnitChange: Function,
            onDelete: Function,
            onUploadFailed: Function,
            queueBus: {
                default: null,
                type: Object
            },
            albumId: {
                type: String,
                required: false
            },
            fixedCropRatio: {
                type: Boolean,
                default: true
            },
            isEditLogo: {
                type: Boolean,
                default: false
            },
        },

        data: function() {
            return {
                status: this.contentUnit.file_name ? 'ready' : 'waiting',
                isSaving: false,
                imageAttributes: {
                    width: this.contentUnit ? this.contentUnit.image_width : 0,
                    height: this.contentUnit ? this.contentUnit.image_height : 0
                },
                imageData: null,
                imageDataForCropping: null,
                canvas: null,
                cropOutputType: 'jpeg'
            }
        },

        computed: {
            imageUrl: function() {
                if (this.contentUnit.file_name) {
                    return this.context.storageEndpoint + this.contentUnit.file_name;
                }
                return null;
            },
            thumbnailUrl: function() {
                if (this.contentUnit.thumbnail_file_name) {
                    return this.context.storageEndpoint + this.contentUnit.thumbnail_file_name;
                }
                return null;
            }
        },

        mounted() {
            if (this.contentUnit.fileObject) {
                if (this.enableCrop) {
                    this.loadImageWithOrientationFixed(this.contentUnit.fileObject, (canvas) => {
                        this.imageDataForCropping = canvas.toDataURL(this.contentUnit.fileObject.type, 1);
                        this.cropOutputType = this.contentUnit.fileObject.type.replace('image/', '');
                        this.$refs['cropper-modal'].show();
                        this.$nextTick(() => {
                            this.$refs.cropper.startCrop();
                        });
                    });
                } else {
                    this.save();
                }
            }
        },

        watch: {
            'contentUnit.fileObject': function(newValue, oldValue) {
                if (this._isBeingDestroyed) {
                    return;
                }

                if (newValue && !oldValue) {
                    if (this.enableCrop) {
                        this.loadImageWithOrientationFixed(this.contentUnit.fileObject, (canvas) => {
                            this.imageDataForCropping = canvas.toDataURL(this.contentUnit.fileObject.type, 1);
                            this.cropOutputType = this.contentUnit.fileObject.type.replace('image/', '');

                            this.$nextTick(() => {
                                this.$refs.cropper.startCrop();
                                // this is to ensure the img is getting reloaded and crop content will be shown
                                // it's a bug/feature of vue-cropper because it caches img
                                this.$refs.cropper.reload();
                            })
                            this.$refs['cropper-modal'].show();
                        });
                    } else {
                        this.save();
                    }
                }
            }
        },

        methods: {
            save() {
                if (this.isSaving) {
                    return;
                }

                this.isSaving = true;
                if (this.queueBus) {
                    this.queueBus.$emit(QueueEvent.ADD_TO_QUEUE, {
                        id: this.contentUnit.id,
                        task: this._actuallySave.bind(this)
                    });
                } else {
                    this._actuallySave();
                }
            },

            _actuallySave() {
                if (Vue.dingIsLocalId(this.contentUnit.id)) {
                    this.getFormData((formData) => {
                        this.createContentUnit(this.providerId, this.providerInformationBlockId, formData, this.onSaveContentUnit, this.albumId);
                    });
                } else {
                    this.getFormData((formData) => {
                        this.updateContentUnit(this.providerId, this.contentUnit.id, formData, this.onSaveContentUnit);
                    });
                }
            },

            getFormData(callback) {
                var data = new FormData();
                data.append('text', this.contentUnit.text || '');
                data.append('is_edit_logo', this.isEditLogo);

                if (this.imageData) {
                    data.append('image_width', this.imageAttributes.width);
                    data.append('image_height', this.imageAttributes.height);
                    data.append('image', this.dataURItoBlob(this.imageData));
                    callback(data);
                } else if (this.contentUnit.fileObject) {
                    this.loadImageWithOrientationFixed(this.contentUnit.fileObject, (canvas) => {
                        canvas.toBlob((blob) => {
                            data.append('image_width', this.imageAttributes.width);
                            data.append('image_height', this.imageAttributes.height);
                            data.append('image', blob);
                            callback(data);
                        }, this.contentUnit.fileObject.type, 1);
                    });
                } else {
                    data.append('image_width', this.imageAttributes.width);
                    data.append('image_height', this.imageAttributes.height);
                    callback(data);
                }
            },

            loadImageWithOrientationFixed(fileObject, cb) {
                loadImage(this.contentUnit.fileObject, (canvas) => {
                    this.imageAttributes.width = canvas.width;
                    this.imageAttributes.height = canvas.height;
                    cb(canvas);
                }, {
                    canvas: true,
                    orientation: true,
                    maxWidth: this.imageMaxLength,
                    maxHeight: this.imageMaxLength
                });
            },

            onSaveContentUnit(response, error) {
                this.isSaving = false;
                $(this.$refs['file-input']).val(null);
                if (error) {
                    if (this.onUploadFailed) {
                        this.onUploadFailed(this.contentUnit);
                    }
                    if (this.queueBus) {
                        this.queueBus.$emit(QueueEvent.REPORT_STATE, {
                            id: this.contentUnit.id,
                            state: 'failed'
                        })
                    }
                } else {
                    const oldContentUnitId = this.contentUnit.id;
                    this.contentUnit.id = response.data.content_unit.id;
                    this.contentUnit.text = response.data.content_unit.text || '';
                    this.contentUnit.file_name = response.data.content_unit.file_name;
                    this.contentUnit.image_width = response.data.content_unit.image_width;
                    this.contentUnit.image_height = response.data.content_unit.image_height;
                    this.contentUnit.thumbnail_file_name = response.data.content_unit.thumbnail_file_name;
                    this.contentUnit.thumbnail_width = response.data.content_unit.thumbnail_width;
                    this.contentUnit.thumbnail_height = response.data.content_unit.thumbnail_height;
                    // reset fileObject to reduce loading when user opening this album
                    this.contentUnit.fileObject = null;
                    this.imageData = null;

                    if (this.onContentUnitChange) {
                        this.onContentUnitChange(this.contentUnit, oldContentUnitId, response.data.favicon_hash);
                    }

                    if (this.queueBus) {
                        this.queueBus.$emit(QueueEvent.REPORT_STATE, {
                            id: oldContentUnitId,
                            state: 'finished'
                        })
                    }
                }
                this.status = 'ready';

                this.context.bus.$emit('requestSavingBlock', this.providerInformationBlockId);
            },

            onCropDone() {
                this.$refs.cropper.getCropData((data) => {
                    this.imageData = data;
                    this.endCrop();

                    var i = new Image();
                    i.onload = () => {
                        this.imageAttributes.width = i.width;
                        this.imageAttributes.height = i.height;
                        this.save();
                    };
                    i.src = data;
                });
            },

            endCrop() {
                if (this.$refs.cropper) {
                    this.$refs.cropper.endCrop();
                }
            },

            onCropHidden() {
                // Discard the selected file object
                this.contentUnit.fileObject = null;
                this.imageDataForCropping = null;
                $(this.$refs['file-input']).val(null);
            },

            onDeleteClick() {
                if (Vue.dingIsLocalId(this.contentUnit.id)) {
                    this.onDelete && this.onDelete(this.contentUnit.id);
                } else {
                    this.promptConfirm(this.$t('message.are_you_sure_to_delete'), this.$t('general.delete'), () => {
                        this.deleteContentUnit(this.providerId, this.contentUnit.id, (response, error) => {
                            if (error) {
                                if (error.response && error.response.status == 404) {
                                    this.onDelete(this.contentUnit.id);
                                } else {
                                    this.promptAlert(error);
                                }
                            } else if (this.onDelete) {
                                this.onDelete(this.contentUnit.id);
                            }
                        });
                    });
                }
            },

            dataURItoBlob(dataURI) {
                var byteString;
                if (dataURI.split(',')[0].indexOf('base64') >= 0) {
                    byteString = atob(dataURI.split(',')[1]);
                } else {
                    byteString = unescape(dataURI.split(',')[1]);
                }

                // separate out the mime component
                var mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

                // write the bytes of the string to a typed array
                var ia = new Uint8Array(byteString.length);
                for (var i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }

                return new Blob([ia], { type:mimeString });
            },

            promptImageSelector() {
                this.$refs['file-input'].click();
            },

            onTextChange(newText) {
                this.contentUnit.text = newText;
                this.save();
            },

            onFileChange() {
                let input = this.$refs['file-input'];
                if (input.files.length > 1) {
                    console.warn('Multiple file selection is not supported by ContentUnit component. Only the first one will be saved.');
                }

                if (input.files.length > 0) {
                    var firstFile = input.files[0];
                    var type = firstFile.type;
                    if (type.match('image/jpeg') || type.match('image/png')) {
                        let sizeMB = Math.round(firstFile.size / 1000000 * 10) / 10;
                        if (sizeMB > Constants.Defaults.MaxUploadFileSizeMB) {
                            this.promptAlert(this.$t('message.file_should_smaller_than_25_mb', {fileName: firstFile.name, size: sizeMB}));
                            return;
                        }

                        this.$set(this.contentUnit, 'fileObject', firstFile);
                    } else {
                        this.promptAlert(this.$t('message.only_image_supported'));
                    }
                }

                this.$forceUpdate();
            }
        }
    }
</script>

<style scoped>
    input {
        display: none;
    }

    .cropper-container {
        position: fixed;
        left: 8px;
        right: 8px;
        bottom: 8px;
        top: 8px;
        z-index: 10;
    }
</style>
